/* eslint-disable @typescript-eslint/no-explicit-any */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

import type { SaleItem } from './types';

// Resolve all GLB asset URLs at module level (Vite returns hashed URLs)
const glbModules = import.meta.glob('../../assets/glb/*.glb', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

function resolveGlbUrl(filename: string): string | null {
  const key = Object.keys(glbModules).find((k) => k.endsWith('/' + filename));
  return key ? glbModules[key] : null;
}

function getGlbName(name: string): string {
  return name.replace(/ /g, '_') + '.glb';
}

function isDesktop(): boolean {
  return window.innerWidth > 768;
}

// Shared DRACO loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');

const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

// Model cache
const modelCache: Record<string, THREE.Group> = {};

interface Slot {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer;
  controls: OrbitControls;
  ambientLight: THREE.AmbientLight;
  hemiLight: THREE.HemisphereLight;
  dirLight1: THREE.DirectionalLight;
  dirLight2: THREE.DirectionalLight;
  fillLights: THREE.PointLight[];
  centerLight: THREE.PointLight;
  container: HTMLDivElement;
  canvas: HTMLCanvasElement;
  slotIdx: number;
  model: THREE.Group | null;
  dcConfig: SaleItem | null;
  _loadId: number;
  _loading: boolean;
  _currentDcIndex: number;
  _desiredDcIndex?: number;
  _clonedMats: THREE.Material[] | null;
  _initRotY: number;
  // Float effects
  baseModelY: number;
  modelMaxDim: number;
  dustParticles: THREE.Points | null;
  // Glow sprites
  glowSprites: THREE.Points | null;
  // Core anim
  coreObj: any;
  coreBaseEmissive: number;
  cageObj: any;
  // Ring anim
  ringTop: any[] | null;
  ringBottom: any[] | null;
  ringAmp: number;
  // Halo spin
  haloMeshes: any[] | null;
  // Inner orbit
  innerOrbitPivot: THREE.Group | null;
  // Force field
  ffMeshes: any[] | null;
  ffDiscs: THREE.Mesh[] | null;
  ffDiscStart: number;
  ffDiscEnd: number;
  _ffColor: THREE.Color | null;
  // Visibility (hidden cards skip rendering)
  visible: boolean;
  // Spinner callback
  onSpinner?: (hidden: boolean) => void;
}

let envMapCache: THREE.Texture | null = null;

function generateEnvMap(renderer: THREE.WebGLRenderer): THREE.Texture {
  if (envMapCache) return envMapCache;
  const pmremGen = new THREE.PMREMGenerator(renderer);
  pmremGen.compileEquirectangularShader();
  const envScene = new THREE.Scene();
  envScene.add(new THREE.AmbientLight(0xddeeff, 6.0));
  const dirs = [
    { color: 0xffffff, int: 8.0, pos: [1, 2, 3] },
    { color: 0x8899cc, int: 5.0, pos: [-2, -1, -2] },
    { color: 0xaabbff, int: 4.0, pos: [0, -3, 1] },
    { color: 0xccddff, int: 4.0, pos: [3, 0, -2] },
    { color: 0xffffff, int: 6.0, pos: [0, 3, 0] },
  ];
  dirs.forEach((d) => {
    const l = new THREE.DirectionalLight(d.color, d.int);
    l.position.set(d.pos[0], d.pos[1], d.pos[2]);
    envScene.add(l);
  });
  envMapCache = pmremGen.fromScene(envScene, 0.04).texture;
  pmremGen.dispose();
  envScene.clear();
  return envMapCache;
}

export class SaleViewer {
  private slots: Slot[] = [];
  private animId: number | null = null;
  private active = false;

  createSlots(
    containers: HTMLDivElement[],
    canvases: HTMLCanvasElement[],
    spinnerCallbacks: ((hidden: boolean) => void)[]
  ) {
    if (this.slots.length > 0) return;
    const count = isDesktop() ? 3 : 1;
    for (let i = 0; i < count; i++) {
      this.slots.push(this.createSlot(i, containers[i], canvases[i], spinnerCallbacks[i]));
    }
  }

  private createSlot(
    slotIdx: number,
    container: HTMLDivElement,
    canvas: HTMLCanvasElement,
    onSpinner: (hidden: boolean) => void
  ): Slot {
    const w = container.clientWidth || 380;
    const h = container.clientHeight || 260;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, w / h, 0.01, 5000);
    camera.position.set(0, 0.02, 0.06);

    const desktop = isDesktop();
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: desktop,
      alpha: true,
      logarithmicDepthBuffer: desktop,
    });
    renderer.setSize(w, h);
    renderer.setPixelRatio(pixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.5;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    const hemiLight = new THREE.HemisphereLight(0xddeeff, 0x445566, 0);
    scene.add(hemiLight);
    const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.5);
    dirLight1.position.set(3, 5, 4);
    scene.add(dirLight1);
    const dirLight2 = new THREE.DirectionalLight(0x88ccff, 0.5);
    dirLight2.position.set(-3, -2, -4);
    scene.add(dirLight2);

    const fillLights: THREE.PointLight[] = [];
    const phi = (1 + Math.sqrt(5)) / 2;
    const fillDirs = [
      [1, 1, 1],
      [-1, 1, 1],
      [1, -1, 1],
      [1, 1, -1],
      [-1, -1, 1],
      [-1, 1, -1],
      [1, -1, -1],
      [-1, -1, -1],
      [0, phi, 1 / phi],
      [0, -phi, 1 / phi],
      [0, phi, -1 / phi],
      [0, -phi, -1 / phi],
      [1 / phi, 0, phi],
      [-1 / phi, 0, phi],
    ];
    fillDirs.forEach((pos) => {
      const pl = new THREE.PointLight(0xccddff, 0, 10);
      const len = Math.sqrt(pos[0] * pos[0] + pos[1] * pos[1] + pos[2] * pos[2]);
      pl.position.set(pos[0] / len, pos[1] / len, pos[2] / len);
      scene.add(pl);
      fillLights.push(pl);
    });

    const centerLight = new THREE.PointLight(0xddeeff, 0, 0);
    centerLight.decay = 0;
    centerLight.position.set(0, 0, 0);
    scene.add(centerLight);

    scene.environment = generateEnvMap(renderer);

    const pw = Math.round(w * pixelRatio);
    const ph = Math.round(h * pixelRatio);
    const renderTarget = new THREE.WebGLRenderTarget(pw, ph, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
    });
    const renderPass = new RenderPass(scene, camera);
    renderPass.clearAlpha = 0;
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(pw, ph), 0.4, 0.4, 0.85);
    const composer = new EffectComposer(renderer, renderTarget);
    composer.setPixelRatio(pixelRatio);
    composer.addPass(renderPass);
    composer.addPass(bloomPass);

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.enableZoom = true;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 3.0;

    return {
      scene,
      camera,
      renderer,
      composer,
      controls,
      ambientLight,
      hemiLight,
      dirLight1,
      dirLight2,
      fillLights,
      centerLight,
      container,
      canvas,
      slotIdx,
      model: null,
      dcConfig: null,
      _loadId: 0,
      _loading: false,
      _currentDcIndex: -1,
      _clonedMats: null,
      _initRotY: 0,
      baseModelY: 0,
      modelMaxDim: 0,
      dustParticles: null,
      glowSprites: null,
      coreObj: null,
      coreBaseEmissive: 0,
      cageObj: null,
      ringTop: null,
      ringBottom: null,
      ringAmp: 3.0,
      haloMeshes: null,
      innerOrbitPivot: null,
      ffMeshes: null,
      ffDiscs: null,
      ffDiscStart: -3.0,
      ffDiscEnd: 7.0,
      _ffColor: null,
      visible: true,
      onSpinner,
    };
  }

  loadModelIntoSlot(slot: Slot, dcIndex: number, dcData: SaleItem[]) {
    slot._desiredDcIndex = dcIndex;
    if (slot._currentDcIndex === dcIndex && slot.model && !slot._loading) return;
    if (slot._loading) return;
    slot._loading = true;
    this.loadModelImpl(slot, dcIndex, dcData);
  }

  private fitCamera(slot: Slot, model: THREE.Group, dc: SaleItem) {
    const z = dc.zoom || 1.0;
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const dist = maxDim * 1.8 * z;

    model.position.sub(center);
    slot.camera.position.set(0, dist * (dc.camY !== undefined ? dc.camY : 0.3), dist);

    const r = maxDim * 0.8;
    slot.fillLights.forEach((pl) => {
      const dir = pl.position.clone().normalize();
      pl.position.copy(dir.multiplyScalar(r));
      pl.distance = r * 3;
    });
    slot.centerLight.position.set(0, 0, 0);
    slot.centerLight.distance = 0;
    slot.controls.target.set(0, 0, 0);
    slot.controls.minDistance = dist * 0.3;
    slot.controls.maxDistance = dist * 5;
    slot.controls.update();
  }

  private addFloatEffects(slot: Slot, dc: SaleItem) {
    if (!dc.float || !slot.model) return;
    const box = new THREE.Box3().setFromObject(slot.model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    slot.baseModelY = slot.model.position.y;
    slot.modelMaxDim = maxDim;

    const dustCount = 100;
    const dustGeom = new THREE.BufferGeometry();
    const dustPos = new Float32Array(dustCount * 3);
    for (let j = 0; j < dustCount; j++) {
      dustPos[j * 3] = (Math.random() - 0.5) * maxDim * 3;
      dustPos[j * 3 + 1] = (Math.random() - 0.5) * maxDim * 2;
      dustPos[j * 3 + 2] = (Math.random() - 0.5) * maxDim * 3;
    }
    dustGeom.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({
      color: 0xccddff,
      size: maxDim * 0.015,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const dust = new THREE.Points(dustGeom, dustMat);
    slot.dustParticles = dust;
    slot.scene.add(dust);
  }

  private addGlowSprites(slot: Slot, dc: SaleItem) {
    if (!(dc as any).glowSprites || !slot.model) return;
    const gs = (dc as any).glowSprites;
    const box = new THREE.Box3().setFromObject(slot.model);
    const size = box.getSize(new THREE.Vector3());
    const count = gs.count || 60;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const colorArr = gs.colors || [0x2288ff, 0xffffff];
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * size.x * 0.85;
      positions[i * 3 + 1] = (Math.random() - 0.5) * size.y * 0.85;
      positions[i * 3 + 2] = (Math.random() - 0.5) * size.z * 0.85;
      const c = new THREE.Color(colorArr[i % colorArr.length]);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.PointsMaterial({
      size: gs.size || 0.3,
      vertexColors: true,
      transparent: true,
      opacity: gs.intensity || 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    const points = new THREE.Points(geom, mat);
    slot.model!.add(points);
    slot.glowSprites = points;
  }

  private clearSlotModel(slot: Slot) {
    if (slot._clonedMats) {
      slot._clonedMats.forEach((m) => m.dispose());
      slot._clonedMats = null;
    }
    if (slot.ffDiscs) {
      slot.ffDiscs.forEach((disc) => {
        if (disc.parent) disc.parent.remove(disc);
        if (disc.geometry) disc.geometry.dispose();
        if (disc.material) (disc.material as THREE.Material).dispose();
      });
      slot.ffDiscs = null;
    }
    slot.ffMeshes = null;
    slot.innerOrbitPivot = null;
    if (slot.glowSprites) {
      if (slot.glowSprites.parent) slot.glowSprites.parent.remove(slot.glowSprites);
      if (slot.glowSprites.geometry) slot.glowSprites.geometry.dispose();
      if (slot.glowSprites.material) (slot.glowSprites.material as THREE.Material).dispose();
      slot.glowSprites = null;
    }
    if (slot.dustParticles) {
      slot.scene.remove(slot.dustParticles);
      if (slot.dustParticles.geometry) slot.dustParticles.geometry.dispose();
      if (slot.dustParticles.material) (slot.dustParticles.material as THREE.Material).dispose();
      slot.dustParticles = null;
    }
    if (slot.model) {
      slot.scene.remove(slot.model);
      slot.model = null;
    }
    slot.coreObj = null;
    slot.coreBaseEmissive = 0;
    slot.cageObj = null;
    slot.ringTop = null;
    slot.ringBottom = null;
    slot.haloMeshes = null;
  }

  private setupModelEffects(
    slot: Slot,
    dc: SaleItem,
    model: THREE.Group,
    trackClone: (m: any) => any
  ) {
    if (dc.initRotY) model.rotation.y = dc.initRotY;
    slot._initRotY = dc.initRotY || 0;
    this.fitCamera(slot, model, dc);
    this.addFloatEffects(slot, dc);
    this.addGlowSprites(slot, dc);

    if (dc.coreAnim) {
      let coreFound: any = null;
      let cageFound: any = null;
      model.traverse((child: any) => {
        if (child.isMesh && child.material) {
          if (child.material.name === 'Energy') coreFound = child;
          if (child.name === 'Mesh_1') cageFound = child;
        }
      });
      if (coreFound) {
        slot.coreObj = coreFound;
        slot.coreObj.material = trackClone(slot.coreObj.material);
        slot.coreBaseEmissive = slot.coreObj.material.emissiveIntensity || 1.0;
      }
      if (cageFound) slot.cageObj = cageFound;
    }

    if (dc.ringAnim) {
      slot.ringTop = [];
      slot.ringBottom = [];
      slot.ringAmp = dc.ringAmp || 3.0;
      const topNames = dc.ringTop || ['Gate_RLExtr', 'Gate'];
      const botNames = dc.ringBottom || ['Gate_RLExtr001', 'Gate001'];
      model.traverse((child: any) => {
        if (child.isMesh) {
          if (topNames.includes(child.name)) {
            slot.ringTop!.push(child);
            child._baseY = child.position.y;
          }
          if (botNames.includes(child.name)) {
            slot.ringBottom!.push(child);
            child._baseY = child.position.y;
          }
        }
      });
    }

    if (dc.haloSpin) {
      slot.haloMeshes = [];
      model.traverse((child: any) => {
        if (child.isMesh && (child.name === 'Halo' || child.name === 'Halo_Haul_Details')) {
          slot.haloMeshes!.push(child);
        }
      });
    }

    if (dc.innerOrbit) {
      const pivot = new THREE.Group();
      const names = dc.innerOrbit;
      const meshes: any[] = [];
      model.traverse((child: any) => {
        if (child.isMesh && names.includes(child.name)) meshes.push(child);
      });
      meshes.forEach((m) => {
        const p = m.parent;
        const px = m.position.x,
          py = m.position.y,
          pz = m.position.z;
        p.remove(m);
        m.position.set(px, py, pz);
        pivot.add(m);
      });
      model.add(pivot);
      slot.innerOrbitPivot = pivot;
    }

    if (dc.forceField) {
      slot.ffMeshes = [];
      model.traverse((child: any) => {
        if (child.isMesh && (child.name === 'Cylinder001' || child.name === 'Cylinder001_RLExtr')) {
          child.material = trackClone(child.material);
          child.material.transparent = true;
          child.material.opacity = 1.0;
          child._ffBaseEmissive = child.material.emissiveIntensity || 0;
          child._ffBaseColor = child.material.emissive
            ? child.material.emissive.clone()
            : new THREE.Color(0x000000);
          slot.ffMeshes!.push(child);
        }
      });
      const discCount = 54;
      slot.ffDiscs = [];
      slot.ffDiscStart = -3.0;
      slot.ffDiscEnd = 7.0;
      for (let i = 0; i < discCount; i++) {
        const geom = new THREE.CircleGeometry(0.9, 32);
        const mat = new THREE.MeshBasicMaterial({
          color: 0x22ddff,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          side: THREE.DoubleSide,
        });
        const disc = new THREE.Mesh(geom, mat);
        disc.scale.set(1, 0.625, 1);
        disc.visible = false;
        model.add(disc);
        slot.ffDiscs.push(disc);
      }
    }
  }

  private loadModelImpl(slot: Slot, dcIndex: number, dcData: SaleItem[]) {
    const dc = dcData[dcIndex];
    const glbName = dc.glb || getGlbName(dc.name);
    slot._loading = true;
    slot._loadId = (slot._loadId || 0) + 1;
    const loadId = slot._loadId;
    slot.dcConfig = dc;

    if (dc.float) {
      slot.controls.autoRotate = false;
    } else {
      slot.controls.autoRotate = true;
    }

    slot.ambientLight.intensity = 0.6 * (dc.amb || 1.0);
    slot.dirLight1.intensity = 1.5 * (dc.dir || 1.0);
    slot.dirLight2.intensity = 0.5 * (dc.dir || 1.0);
    slot.hemiLight.intensity = dc.hemi || 0;
    slot.fillLights.forEach((pl) => {
      pl.intensity = dc.fill || 0;
    });
    slot.centerLight.intensity = dc.center || 0;

    this.clearSlotModel(slot);
    slot._clonedMats = [];
    const trackClone = (mat: any) => {
      const c = mat.clone();
      slot._clonedMats!.push(c);
      return c;
    };

    // Check cache
    if (modelCache[glbName]) {
      const cached = modelCache[glbName];
      slot.model = cached.clone();
      cached.traverse((orig: any) => {
        if (orig.isMesh) {
          const cloned: any = slot.model!.getObjectByName(orig.name);
          if (cloned && cloned.isMesh) cloned.material = orig.material;
        }
      });
      slot.scene.add(slot.model);
      this.setupModelEffects(slot, dc, slot.model, trackClone);
      slot.onSpinner?.(true);
      this.slotLoadDone(slot, dcIndex, dcData);
      return;
    }

    slot.renderer.clear();
    slot.onSpinner?.(false);

    const url = resolveGlbUrl(glbName);
    if (!url) {
      console.warn('GLB not found:', glbName);
      slot.onSpinner?.(true);
      this.slotLoadDone(slot, dcIndex, dcData);
      return;
    }

    gltfLoader.load(
      url,
      (gltf) => {
        if (slot._loadId !== loadId) {
          gltf.scene.traverse((child: any) => {
            if (child.isMesh) {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                ['map', 'emissiveMap', 'normalMap', 'metalnessMap', 'roughnessMap'].forEach((k) => {
                  if (child.material[k]) child.material[k].dispose();
                });
                child.material.dispose();
              }
            }
          });
          return;
        }
        const model = gltf.scene;
        const envInt = dc.envInt || 2.5;
        let glowColorIdx = 0;
        model.traverse((child: any) => {
          if (child.isMesh && child.material) {
            if (child.material.emissiveMap && dc.glowColors && dc.glowColors.length > 0) {
              child.material = child.material.clone();
              const density = dc.glowDensity || 1.0;
              if (density >= 1.0 || glowColorIdx % Math.round(1 / density) === 0) {
                child.material.emissive = new THREE.Color(
                  dc.glowColors[glowColorIdx % dc.glowColors.length]
                );
                child.material.emissiveIntensity = Math.max(
                  child.material.emissiveIntensity,
                  dc.glowInt || 7.5
                );
              } else {
                child.material.emissiveIntensity = 0;
              }
              glowColorIdx++;
            } else if (child.material.emissiveMap) {
              child.material.emissiveIntensity = Math.max(
                child.material.emissiveIntensity,
                dc.glowInt || 7.5
              );
            } else if (
              child.material.emissive &&
              (child.material.emissive.r > 0 ||
                child.material.emissive.g > 0 ||
                child.material.emissive.b > 0)
            ) {
              child.material.emissiveIntensity = Math.max(
                child.material.emissiveIntensity,
                dc.emissive || 3.0
              );
            } else if (dc.emissive) {
              child.material.emissive = new THREE.Color(0x334466);
              child.material.emissiveIntensity = dc.emissive * 0.3;
            }
            child.material.envMapIntensity = envInt;
            const isDetail = /RLExtr|Details|Lights/.test(child.name);
            child.material.polygonOffset = true;
            child.material.polygonOffsetFactor = isDetail ? 0 : 2;
            child.material.polygonOffsetUnits = isDetail ? 0 : 2;
            if (dc.shellTint && child.name === 'steel') {
              child.material = child.material.clone();
              child.material.color = new THREE.Color(dc.shellTint);
              child.material.metalness = 0.7;
              child.material.roughness = 0.5;
              child.material.envMapIntensity = 1.5;
              child.material.emissive = new THREE.Color(0x333844);
              child.material.emissiveIntensity = 0.5;
              child.material.polygonOffset = false;
              if (child.material.specularIntensity !== undefined)
                child.material.specularIntensity = 0;
              if (child.material.clearcoat !== undefined) child.material.clearcoat = 0;
              if (child.material.sheen !== undefined) child.material.sheen = 0;
            }
            child.material.needsUpdate = true;
          }
        });

        // Cache eviction
        const maxCache = isDesktop() ? 5 : 3;
        const cacheKeys = Object.keys(modelCache);
        if (cacheKeys.length >= maxCache) {
          const inUse = new Set<string>();
          this.slots.forEach((s) => {
            if (s.dcConfig) inUse.add(s.dcConfig.glb || getGlbName(s.dcConfig.name));
          });
          inUse.add(glbName);
          for (let ck = 0; ck < cacheKeys.length; ck++) {
            if (!inUse.has(cacheKeys[ck])) {
              const evictModel = modelCache[cacheKeys[ck]];
              evictModel.traverse((child: any) => {
                if (child.isMesh) {
                  if (child.geometry) child.geometry.dispose();
                  if (child.material) {
                    [
                      'map',
                      'emissiveMap',
                      'normalMap',
                      'metalnessMap',
                      'roughnessMap',
                      'aoMap',
                    ].forEach((k) => {
                      if (child.material[k]) {
                        child.material[k].dispose();
                        child.material[k] = null;
                      }
                    });
                    child.material.dispose();
                  }
                }
              });
              delete modelCache[cacheKeys[ck]];
              break;
            }
          }
        }

        modelCache[glbName] = model;
        slot.model = model;
        slot.scene.add(slot.model);
        this.setupModelEffects(slot, dc, slot.model, trackClone);
        slot.onSpinner?.(true);
        this.slotLoadDone(slot, dcIndex, dcData);
      },
      undefined,
      (err) => {
        console.warn('Sale model load error:', glbName, err);
        slot.onSpinner?.(true);
        this.slotLoadDone(slot, dcIndex, dcData);
      }
    );
  }

  private slotLoadDone(slot: Slot, dcIndex: number, dcData: SaleItem[]) {
    slot._loading = false;
    slot._currentDcIndex = dcIndex;
    if (slot._desiredDcIndex !== undefined && slot._desiredDcIndex !== dcIndex) {
      const next = slot._desiredDcIndex;
      slot._loading = true;
      this.loadModelImpl(slot, next, dcData);
    }
  }

  startAnimation() {
    this.active = true;
    if (!this.animId) this.animate();
  }

  stopAnimation() {
    this.active = false;
    if (this.animId) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
  }

  private animate = () => {
    if (!this.active) {
      this.animId = null;
      return;
    }
    this.animId = requestAnimationFrame(this.animate);
    const t = performance.now() * 0.001;

    this.slots.forEach((slot) => {
      if (!slot.visible || slot._loading || !slot.model) return;
      try {
        const dc = slot.dcConfig;

        // Float animation
        if (dc && dc.float && slot.model && slot.modelMaxDim) {
          const amp = slot.modelMaxDim * 0.07;
          const floatY = Math.sin(t * 0.5) * amp;
          const tiltX = Math.sin(t * 0.3) * 0.02;
          const tiltZ = Math.cos(t * 0.25) * 0.015;
          slot.model.position.y = (slot.baseModelY || 0) + floatY;
          slot.model.rotation.x = tiltX;
          slot.model.rotation.z = tiltZ;
          slot.model.rotation.y = (slot.model.rotation.y + 0.0008) % (Math.PI * 2);
        }

        // Energy core + cage animation
        if (slot.coreObj) {
          slot.coreObj.rotation.y = (slot.coreObj.rotation.y - 0.006) % (Math.PI * 2);
          slot.coreObj.rotation.x = Math.sin(t * 0.7) * 0.1;
          const scVal = Math.sin(t * 0.9);
          const sc = 1.0 + scVal * 0.12;
          const dimFactor = scVal > 0 ? 1.0 - scVal * 0.7 : 1.0;
          const pulse =
            (slot.coreBaseEmissive || 1.0) * (1.0 + Math.sin(t * 1.2) * 0.4) * dimFactor;
          slot.coreObj.material.emissiveIntensity = pulse;
          slot.coreObj.scale.set(sc, sc, sc);
        }
        if (slot.cageObj) {
          slot.cageObj.rotation.y = (slot.cageObj.rotation.y - 0.006) % (Math.PI * 2);
          slot.cageObj.rotation.x = Math.sin(t * 0.7) * 0.1;
          const sc = 1.0 + Math.sin(t * 0.9) * 0.12;
          slot.cageObj.scale.set(sc, sc, sc);
        }

        // Ring slide
        if (slot.ringTop && slot.ringTop.length > 0) {
          const ringOffset = Math.sin(t * 0.4) * (slot.ringAmp || 3.0);
          slot.ringTop.forEach((m: any) => {
            m.position.y = (m._baseY || 0) + ringOffset;
          });
          slot.ringBottom?.forEach((m: any) => {
            m.position.y = (m._baseY || 0) - ringOffset;
          });
        }

        // Halo spin
        if (slot.haloMeshes) {
          slot.haloMeshes.forEach((m: any) => {
            m.rotation.z = (t * 24.0) % (Math.PI * 2);
          });
        }

        // Inner orbit
        if (slot.innerOrbitPivot) {
          slot.innerOrbitPivot.rotation.x = Math.sin(t * 0.9) * 0.4;
          slot.innerOrbitPivot.rotation.y = (t * 1.44) % (Math.PI * 2);
          slot.innerOrbitPivot.rotation.z = Math.cos(t * 0.75) * 0.3;
        }

        // Force field
        if (slot.ffMeshes && slot.ffMeshes.length > 0) {
          const cycle = t % 8.0;
          let ffIntensity = 0;
          if (cycle < 1.0) {
            ffIntensity = cycle;
          } else if (cycle < 3.0) {
            ffIntensity = 0.8 + Math.sin(t * 12.0) * 0.2;
          } else if (cycle < 4.0) {
            ffIntensity = 1.0 - (cycle - 3.0);
          }
          const ffColor = slot._ffColor || (slot._ffColor = new THREE.Color(0x22ddff));
          slot.ffMeshes.forEach((m: any) => {
            m.material.emissive = ffColor;
            m.material.emissiveIntensity = ffIntensity * 3.0;
            m.material.opacity = 1.0 - ffIntensity * 0.15;
          });
          if (slot.ffDiscs && slot.ffDiscs.length > 0) {
            const zStart = slot.ffDiscStart;
            const zEnd = slot.ffDiscEnd;
            const zRange = zEnd - zStart;
            const count = slot.ffDiscs.length;
            const cycleNum = Math.floor(t / 8.0);
            const reverse = cycleNum % 2 === 1;
            for (let i = 0; i < count; i++) {
              const disc = slot.ffDiscs[i];
              if (ffIntensity <= 0) {
                disc.visible = false;
                continue;
              }
              const launchT = (i / count) * 3.0;
              const elapsed = cycle - launchT;
              if (elapsed < 0 || elapsed > 1.2) {
                disc.visible = false;
                continue;
              }
              const progress = elapsed / 1.2;
              disc.visible = true;
              const zPos = reverse ? zEnd - progress * zRange : zStart + progress * zRange;
              disc.position.set(0, 0, zPos);
              const fade =
                progress < 0.15 ? progress / 0.15 : progress > 0.85 ? (1 - progress) / 0.15 : 1.0;
              (disc.material as THREE.MeshBasicMaterial).opacity = fade * 0.45 * ffIntensity;
            }
          }
        }

        slot.controls.update();
        slot.composer.render();
      } catch (e: any) {
        console.warn('Sale render error slot', slot.slotIdx, e.message);
      }
    });
  };

  resize() {
    this.slots.forEach((slot) => {
      const w = slot.container.clientWidth;
      const h = slot.container.clientHeight;
      if (w === 0 || h === 0) return;
      slot.camera.aspect = w / h;
      slot.camera.updateProjectionMatrix();
      const pixelRatio = Math.min(window.devicePixelRatio, 2);
      slot.renderer.setPixelRatio(pixelRatio);
      slot.renderer.setSize(w, h);
      slot.composer.setPixelRatio(pixelRatio);
      slot.composer.setSize(w, h);
    });
  }

  setSlotVisible(idx: number, visible: boolean) {
    const slot = this.slots[idx];
    if (slot) slot.visible = visible;
  }

  getSlot(idx: number): Slot | undefined {
    return this.slots[idx];
  }

  get slotCount(): number {
    return this.slots.length;
  }

  destroy() {
    this.stopAnimation();
    this.slots.forEach((slot) => {
      this.clearSlotModel(slot);
      slot.renderer.dispose();
      slot.composer.dispose();
      slot.controls.dispose();
    });
    this.slots = [];
  }
}
